import type { BreadcrumbProps } from '@blueprintjs/core'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useMemo, useState } from 'react'
import { Filters, PerformanceFilters, TableName } from '../../config'
import { useKdpFilters } from '../../hooks/useKdpFilters'
import { useUser } from '../../hooks/useUser'
import type { Portfolio } from '../../types/portfolio'
import type { TableProps } from '../ad_groups/AdGroupsTable'
import { Layout } from '../Layout'
import {
    Columns,
    defaultPerfColumns,
    type ExpandedPerformanceMetrics,
} from '../table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable } from '../table2/AgGridTable'
import { addExtraCols } from '../campaigns/CampaignsTable'
import { Button } from '../ui/button'
import { CreatePortfoliosModal } from './CreatePortfoliosModal'

const defaultFilters = [Filters.CampaignCount, ...PerformanceFilters]

const mutableFields: string[] = []

export const PortfoliosTable = observer(
    ({
        nested,
        store,
        savedFiltersStore,
        tableHooks,
    }: TableProps<Portfolio & ExpandedPerformanceMetrics>): JSX.Element => {
        const useUserValues = useUser()
        const filters = useKdpFilters(defaultFilters)

        const { params } = tableHooks

        const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

        const cols3 = useMemo(() => {
            return newColumns(useUserValues.is_kdp)
        }, [useUserValues.is_kdp])

        // Creation is not selection-based, so it is a standalone button rather
        // than a BulkActions menu item.
        const [createOpen, setCreateOpen] = useState(false)
        const reloadAfterCreate = useCallback(() => tableHooks.reload(), [tableHooks])

        return (
            <Layout
                breadcrumbs={breadcrumbs}
                primary={TableName.Portfolio}
                nested={nested}
                params={params}
            >
                <AgGridTable
                    name={TableName.Portfolio}
                    columns={cols3}
                    tableHooks={tableHooks}
                    store={store}
                    showHeader={false}
                    savedFiltersStore={savedFiltersStore}
                    filters={filters}
                    search
                    searchPlaceholder="Search by portfolio name"
                    columnSelection
                    mutableFields={mutableFields}
                    nested={nested}
                    otherActions={
                        <Button
                            text="Add Portfolios"
                            intent="primary"
                            small
                            onClick={() => setCreateOpen(true)}
                        />
                    }
                />
                <CreatePortfoliosModal
                    isOpen={createOpen}
                    onClose={() => setCreateOpen(false)}
                    onCreated={reloadAfterCreate}
                />
            </Layout>
        )
    },
)

const newColumns = <T,>(isKdp: boolean): AgGridColumnDef<T>[] => {
    const cols: AgGridColumnDef<T>[] = [
        Columns.PortfolioName,
        Columns.CampaignCount,
        ...defaultPerfColumns,
    ]

    if (isKdp) {
        addExtraCols(cols, isKdp)
    }

    return cols.filter(
        (col) =>
            col.field !== 'ideal_cpc' && col.field !== 'ideal_acos' && col.field !== 'target_acos',
    )
}
